import type {
  MyQueueTimeclockAction,
  MyQueueTimeclockData,
} from "./myqueueApi";

export type TimeclockState = "in" | "out" | "lunch" | "break";

/** User-facing labels for each timeclock action — used for buttons and messages. */
export const ACTION_LABELS: Record<MyQueueTimeclockAction, string> = {
  punchin: "Punch In",
  punchout: "Punch Out",
  lunch: "Lunch",
  break: "Break",
  return: "Return",
};

/**
 * Strip everything but letters and digits, lowercase — so "PunchIn",
 * "Punch In", "punch_in", and "PUNCH-IN" all collapse to "punchin".
 * Engage's timeclock endpoints don't document the casing on lastAction
 * and we've observed it not matching the exact lowercase form.
 */
export function normalize(s: string | undefined | null): string {
  return (s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

export function classifyAction(raw: string | undefined | null): TimeclockState | null {
  const v = normalize(raw);
  if (!v) return null;
  if (v === "punchin" || v === "return" || v === "in") return "in";
  if (v === "punchout" || v === "out") return "out";
  if (v === "lunch") return "lunch";
  if (v === "break") return "break";
  return null;
}

/**
 * Single-letter `tcitype` codes Engage uses on each interval entry.
 *   I = punch-in interval (clocked in)
 *   L = lunch
 *   B = break
 * Anything else returns null so resolveState falls back to other signals.
 */
function classifyTcitype(raw: string | undefined | null): TimeclockState | null {
  const v = (raw ?? "").trim().toUpperCase();
  if (v === "I") return "in";
  if (v === "L") return "lunch";
  if (v === "B") return "break";
  return null;
}

/**
 * Resolve the user's clock state.
 *
 * **Entries are authoritative**, not `lastAction`. Engage's `lastAction`
 * field reports the last *started* verb (always "PUNCH IN" once the user
 * has ever punched in) and never updates on a punch-out, so trusting it
 * would leave the UI permanently showing "Clocked In". Each entry is an
 * interval — when its `endTime` is populated the interval is closed and
 * the user is clocked out. Entries arrive most-recent-first so
 * `entries[0]` is the latest.
 */
export function resolveState(data: MyQueueTimeclockData | null | undefined): TimeclockState {
  const entries = data?.timeclockEntries ?? [];
  if (entries.length > 0) {
    const latest = entries[0]!;
    const open = !latest.endTime && !latest.displayEndTime;
    if (!open) return "out";
    return classifyTcitype(latest.tcitype) ?? classifyAction(latest.action) ?? "in";
  }
  // No entries today — fall back to lastAction as a last resort.
  return classifyAction(data?.lastAction) ?? "out";
}

/**
 * Maps a user-initiated action verb to the state it leaves the user in.
 * Used for optimistic updates after a successful POST to /timeclock/{action}.
 */
export function stateAfterAction(action: MyQueueTimeclockAction): TimeclockState {
  switch (action) {
    case "punchin":
    case "return":
      return "in";
    case "punchout":
      return "out";
    case "lunch":
      return "lunch";
    case "break":
      return "break";
  }
}
